import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import type { RegionPreset, ThreatLevel } from '@/types';
import { AudienceSelector } from '@/components/map/AudienceSelector';
import { AnimatedNumber } from '@/components/ui/AnimatedNumber';
import { isAlertMuted, setAlertMuted } from '@/algorithms/breaking-alert';

// ---------------------------------------------------------------------------
// Top header bar  (48 px)
// ---------------------------------------------------------------------------

// 속보 알림음 on/off 토글 — localStorage 영구화
const AlertMuteToggle: FC = () => {
  const [muted, setMuted] = useState(() => isAlertMuted());

  const toggle = () => {
    const next = !muted;
    setAlertMuted(next);
    setMuted(next);
  };

  return (
    <button
      onClick={toggle}
      className={clsx(
        'px-2 py-0.5 rounded text-[12px] border transition-colors',
        muted
          ? 'border-surface-border text-slate-600 hover:text-slate-400'
          : 'border-surface-border text-slate-300 hover:text-white hover:border-surface-accent',
      )}
      title={muted ? 'Alert sound OFF — click to enable' : 'Alert sound ON — click to mute'}
    >
      {muted ? '🔕' : '🔔'}
    </button>
  );
};

const REGIONS: RegionPreset[] = ['global', 'asia', 'europe', 'americas', 'mena', 'africa'];

function riskToLevel(score: number): ThreatLevel {
  if (score >= 70) return 'critical';
  if (score >= 50) return 'high';
  if (score >= 30) return 'medium';
  return 'low';
}

const RISK_COLORS: Record<ThreatLevel, string> = {
  critical: 'text-threat-critical',
  high: 'text-threat-high',
  medium: 'text-threat-medium',
  low: 'text-threat-low',
  info: 'text-threat-info',
};

interface ThreatconLevel {
  short: string;
  color: string;        // text color for label
  segBg: string;        // filled segment color
  badge: string;        // pill bg+border
  scoreRange: string;
  status: string;
  description: string;
  descriptionKo: string;
}

const TC_LEVELS: Record<number, ThreatconLevel> = {
  1: {
    short: 'TC-1', color: 'text-emerald-300', segBg: 'bg-emerald-500',
    badge: 'bg-emerald-950/70 border-emerald-700',
    scoreRange: 'Risk 0–29',
    status: 'Normal',
    description: 'Routine security posture. No specific threat intelligence.',
    descriptionKo: '정상 경계. 특이 위협 없음.',
  },
  2: {
    short: 'TC-2', color: 'text-yellow-300', segBg: 'bg-yellow-500',
    badge: 'bg-yellow-950/70 border-yellow-700',
    scoreRange: 'Risk 30–49',
    status: 'Elevated',
    description: 'Increased security posture. General threat indicators present.',
    descriptionKo: '경계 상향. 일반적 위협 징후 감지.',
  },
  3: {
    short: 'TC-3', color: 'text-amber-300', segBg: 'bg-amber-500',
    badge: 'bg-amber-950/70 border-amber-700',
    scoreRange: 'Risk 50–69',
    status: 'High',
    description: 'Specific threat credible. Heightened security and monitoring.',
    descriptionKo: '구체적 위협 신호. 경계 강화 및 집중 감시.',
  },
  4: {
    short: 'TC-4', color: 'text-orange-300', segBg: 'bg-orange-500',
    badge: 'bg-orange-950/70 border-orange-700',
    scoreRange: 'Risk 70–84',
    status: 'Severe',
    description: 'Imminent threat. Partial mobilization. Crisis management active.',
    descriptionKo: '임박한 위협. 부분 동원. 위기 대응 가동.',
  },
  5: {
    short: 'TC-5', color: 'text-red-300', segBg: 'bg-red-500',
    badge: 'bg-red-950/80 border-red-600',
    scoreRange: 'Risk 85–100',
    status: 'CRITICAL',
    description: 'Attack imminent or in progress. Full mobilization. Maximum alert.',
    descriptionKo: '공격 임박 또는 진행 중. 완전 동원. 최대 경계.',
  },
};

export const Header: FC = () => {
  const { t, i18n } = useTranslation();
  const activeRegion = useAppStore((s) => s.activeRegion);
  const setRegion = useAppStore((s) => s.setRegion);
  const setSimulationActive = useAppStore((s) => s.setSimulationActive);

  // Computed directly from underlying data so re-renders trigger when data updates.
  // (Subscribing to function references in the store never triggers re-renders.)
  const riskScore = useAppStore((s) => {
    const alerts = s.crisisAlerts.filter((a) => !a.dismissed);
    const top5 = [...s.countryRisks].sort((a, b) => b.score - a.score).slice(0, 5);
    const weights = [0.35, 0.25, 0.20, 0.12, 0.08];
    let score = Math.round(top5.reduce((sum, r, i) => sum + r.score * (weights[i] || 0.05), 0));
    const alertBonus = alerts.reduce((acc, a) => acc + (a.level === 'extreme' ? 8 : a.level === 'critical' ? 4 : 1), 0);
    return Math.min(100, score + alertBonus);
  });
  const threatcon = useAppStore((s) => {
    const alerts = s.crisisAlerts.filter((a) => !a.dismissed);
    const top5 = [...s.countryRisks].sort((a, b) => b.score - a.score).slice(0, 5);
    const weights = [0.35, 0.25, 0.20, 0.12, 0.08];
    let score = Math.round(top5.reduce((sum, r, i) => sum + r.score * (weights[i] || 0.05), 0));
    const alertBonus = alerts.reduce((acc, a) => acc + (a.level === 'extreme' ? 8 : a.level === 'critical' ? 4 : 1), 0);
    score = Math.min(100, score + alertBonus);
    if (score >= 85) return 5;
    if (score >= 70) return 4;
    if (score >= 50) return 3;
    if (score >= 30) return 2;
    return 1;
  });
  const eventCount = useAppStore((s) =>
    s.events.length + s.earthquakes.length + s.wildfires.length +
    s.aircraft.length + s.ships.length + s.navalVessels.length,
  );
  // 오늘(00:00 GMT 이후) 누적 이벤트 — 뉴스 + ACLED + 지진 + 속보
  const todayEventCount = useAppStore((s) => {
    const dayStart = new Date();
    dayStart.setUTCHours(0, 0, 0, 0);
    const t0 = dayStart.getTime();
    const isToday = (ts?: string) => {
      if (!ts) return false;
      const t = Date.parse(ts);
      return !Number.isNaN(t) && t >= t0;
    };
    return (
      s.news.filter((n) => isToday(n.publishedAt)).length +
      s.acledEvents.filter((e) => isToday(e.event_date)).length +
      s.earthquakes.filter((e) => isToday(e.timestamp)).length +
      s.breakingAlerts.filter((b) => isToday(b.timestamp)).length
    );
  });
  const riskLevel = riskToLevel(riskScore);
  const tc = TC_LEVELS[threatcon] ?? TC_LEVELS[1];

  const toggleLanguage = useCallback(() => {
    const next = i18n.language === 'en' ? 'ko' : 'en';
    i18n.changeLanguage(next);
    localStorage.setItem('detectx-lang', next);
  }, [i18n]);

  // Mobile scroll arrows
  const headerRef = useRef<HTMLElement>(null);
  const [canScrollL, setCanScrollL] = useState(false);
  // Start true on mobile so the › arrow is visible immediately as a scroll hint
  const [canScrollR, setCanScrollR] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768,
  );

  const updateArrows = useCallback(() => {
    const el = headerRef.current;
    if (!el) return;
    setCanScrollL(el.scrollLeft > 2);
    setCanScrollR(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', updateArrows); ro.disconnect(); };
  }, [updateArrows]);

  const scrollHeader = (dir: 'l' | 'r') =>
    headerRef.current?.scrollBy({ left: dir === 'l' ? -120 : 120, behavior: 'smooth' });

  return (
    <header
      ref={headerRef}
      className="h-12 relative bg-surface-panel/90 backdrop-blur-md border-b border-surface-border z-50 select-none shrink-0 overflow-x-auto"
      style={{ scrollbarWidth: 'none' }}
    >
      {/* Mobile scroll arrows */}
      {canScrollL && (
        <button
          onClick={() => scrollHeader('l')}
          className="absolute left-0 top-0 h-full w-7 z-10 flex md:hidden items-center justify-center text-slate-300 text-sm font-bold pointer-events-auto"
          style={{ background: 'linear-gradient(to right, #080d14 60%, transparent)' }}
        >‹</button>
      )}
      {canScrollR && (
        <button
          onClick={() => scrollHeader('r')}
          className="absolute right-0 top-0 h-full w-8 z-10 flex md:hidden items-center justify-center text-slate-200 text-base font-bold pointer-events-auto animate-pulse"
          style={{ background: 'linear-gradient(to left, #080d14 70%, transparent)' }}
        >›</button>
      )}
      <div className="h-full flex items-center justify-between px-4 gap-4 min-w-max md:min-w-0 md:w-full">
      {/* ---- Left: Logo + THREATCON ---- */}
      <div className="flex items-center gap-3 shrink-0">
        <img src="/logo-40.png" alt="DetectX" className="h-7 w-7 shrink-0" />
        <h1 className="text-sm font-bold tracking-wide text-slate-100" style={{ textShadow: '0 0 12px rgba(59,130,246,0.45)' }}>
          {t('app.name')}
        </h1>
        {/* THREATCON bar */}
        <div className="flex flex-col gap-0.5">
          {/* Label row */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-[8px] uppercase tracking-widest text-slate-500 hidden lg:inline">Threatcon</span>
            <span className={clsx('text-[10px] font-bold tracking-widest leading-none', tc.color, threatcon >= 4 && 'animate-pulse')}>
              {tc.short}
            </span>
          </div>
          {/* 5-segment bar */}
          <div className="flex gap-[3px]">
            {([1, 2, 3, 4, 5] as const).map((lvl) => {
              const seg = TC_LEVELS[lvl];
              const active = lvl <= threatcon;
              const isCurrent = lvl === threatcon;
              return (
                <div key={lvl} className="relative group/seg">
                  <div
                    className={clsx(
                      'h-[5px] w-7 rounded-sm transition-all duration-500 cursor-default',
                      active ? seg.segBg : 'bg-slate-700/40',
                      isCurrent && threatcon >= 4 && 'animate-pulse',
                      isCurrent && 'ring-1 ring-white/25',
                    )}
                  />
                  {/* Tooltip — opens downward (header is at top of viewport) */}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 hidden group-hover/seg:block z-[200] pointer-events-none">
                    {/* Arrow pointing up */}
                    <div className="flex justify-center mb-[-1px]">
                      <div className={clsx(
                        'w-2 h-2 rotate-45 border-t border-l',
                        lvl === 1 ? 'bg-emerald-950/70 border-emerald-700'
                        : lvl === 2 ? 'bg-yellow-950/70 border-yellow-700'
                        : lvl === 3 ? 'bg-amber-950/70 border-amber-700'
                        : lvl === 4 ? 'bg-orange-950/70 border-orange-700'
                        : 'bg-red-950/80 border-red-600',
                      )} />
                    </div>
                    <div className={clsx(
                      'rounded border px-2.5 py-2 text-[10px] shadow-2xl whitespace-nowrap',
                      seg.badge,
                    )}>
                      <div className={clsx('font-bold text-[11px] mb-0.5', seg.color)}>
                        THREATCON {lvl} — {seg.status}
                      </div>
                      <div className="text-slate-400 text-[9px] mb-1">{seg.scoreRange}</div>
                      <div className="text-slate-300 max-w-[200px] whitespace-normal leading-tight">
                        {i18n.language === 'ko' ? seg.descriptionKo : seg.description}
                      </div>
                      {isCurrent && (
                        <div className={clsx('mt-1 text-[9px] font-semibold', seg.color)}>
                          ▼ Current Level
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ---- Center-left: Audience Selector ---- */}
      <div className="hidden md:flex items-center gap-2 ml-2">
        <AudienceSelector />
      </div>

      {/* ---- Center: Region quick-switch ---- */}
      <nav className="hidden xl:flex items-center gap-1">
        {REGIONS.map((r) => (
          <button
            key={r}
            onClick={() => setRegion(r)}
            className={clsx(
              'px-2.5 py-1 rounded text-xs font-medium transition-colors',
              activeRegion === r
                ? 'bg-brand/20 text-brand-light'
                : 'text-slate-400 hover:text-slate-200 hover:bg-surface-hover',
            )}
          >
            {t(`nav.${r}`)}
          </button>
        ))}
      </nav>

      {/* ---- Right: Status items ---- */}
      <div className="flex items-center gap-4">
        {/* Live indicator */}
        <div className="flex items-center gap-1.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">
            {t('status.live')}
          </span>
        </div>

        {/* Active event count */}
        <span className="text-xs text-slate-400">
          <span className="data-mono font-semibold text-slate-200">{eventCount}</span> events
        </span>

        {/* Alert sound mute toggle */}
        <AlertMuteToggle />

        {/* Language toggle */}
        <button
          onClick={toggleLanguage}
          className="px-2 py-0.5 rounded text-[10px] font-medium border border-surface-border text-slate-400 hover:text-slate-200 hover:border-surface-accent transition-colors"
        >
          {i18n.language === 'en' ? 'KO' : 'EN'}
        </button>

        {/* SIMULATE button — Coming Soon */}
        <button
          disabled
          className="relative px-3 py-1.5 rounded text-[11px] font-bold border border-amber-500/50 bg-amber-500/10 text-amber-400 cursor-not-allowed tracking-wide uppercase overflow-hidden"
          title="Simulation Mode — Coming Soon"
          style={{ boxShadow: '0 0 10px rgba(245,158,11,0.25), inset 0 0 10px rgba(245,158,11,0.05)' }}
        >
          <span className="absolute inset-0 rounded animate-pulse bg-amber-400/5" />
          <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{width:'12px',height:'12px',display:'inline',marginRight:'4px',verticalAlign:'-1px'}}>
            <polygon points="2,2 12,7 2,12" />
            <line x1="12" y1="2" x2="12" y2="12" />
          </svg>
          Simulate <span className="text-[9px] font-medium normal-case ml-0.5 text-amber-500/70">soon</span>
        </button>

        {/* Live event odometer — since 00:00 GMT */}
        <div className="flex flex-col items-end gap-0.5 pl-3 border-l border-surface-border">
          <span className="text-[9px] uppercase tracking-[0.25em] text-neon-mint/60 leading-none whitespace-nowrap">
            Events since 00:00 GMT
          </span>
          <AnimatedNumber
            value={todayEventCount}
            className="text-xl leading-none text-neon-mint glow-live rounded px-1"
          />
        </div>

        {/* Strategic Risk Score */}
        <div className="flex items-center gap-1.5 pl-3 border-l border-surface-border">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 hidden lg:inline">Risk</span>
          <span className={clsx('text-lg font-bold data-mono leading-none', RISK_COLORS[riskLevel])}>
            {riskScore}
          </span>
        </div>
      </div>
      </div>
    </header>
  );
};
